import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Search, Users, Edit, Check, X, ChevronDown, Loader2, AlertCircle, ChevronUp, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';

const API_BASE_URL = `${process.env.REACT_APP_API_URL.replace(/\/$/, '')}/api`;

const TrainingUsersContent = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [trainingRoles, setTrainingRoles] = useState([]);
  const [trainingCountries, setTrainingCountries] = useState([]);
  const [trainingSites, setTrainingSites] = useState([]);
  const [companyUsers, setCompanyUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState([]);
  const [countryFilter, setCountryFilter] = useState([]);
  const [siteFilter, setSiteFilter] = useState([]);
  const [managerFilter, setManagerFilter] = useState([]);
  const [supervisorFilter, setSupervisorFilter] = useState([]);
  const [coordinatorFilter, setCoordinatorFilter] = useState([]);
  
  // Bulk operations
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [bulkRole, setBulkRole] = useState('');
  const [bulkCountry, setBulkCountry] = useState('');
  const [bulkSite, setBulkSite] = useState('');
  const [bulkManager, setBulkManager] = useState('');
  const [bulkSupervisor, setBulkSupervisor] = useState('');
  const [bulkCoordinator, setBulkCoordinator] = useState('');
  
  // Individual user changes tracking
  const [userChanges, setUserChanges] = useState({});
  const [savingUsers, setSavingUsers] = useState([]);

  // Sorting state
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  
  // Dropdown states
  const [dropdownOpen, setDropdownOpen] = useState({
    roles: false,
    countries: false,
    sites: false,
    managers: false,
    supervisors: false,
    coordinators: false
  });

  // Sorting function
  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new field with ascending direction
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Get sort icon
  const getSortIcon = (field) => {
    if (sortField !== field) {
      return <ArrowUpDown className="h-4 w-4 text-slate-400" />;
    }
    return sortDirection === 'asc' 
      ? <ArrowUp className="h-4 w-4 text-brand-500" />
      : <ArrowDown className="h-4 w-4 text-brand-500" />;
  };

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        const [usersResponse, rolesResponse, countriesResponse, sitesResponse] = await Promise.all([
          axios.get(`${API_BASE_URL}/users/`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
          }),
          axios.get(`${API_BASE_URL}/training-roles/`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
          }),
          axios.get(`${API_BASE_URL}/training-countries/`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
          }),
          axios.get(`${API_BASE_URL}/training-sites/`, {
            headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
          })
        ]);

        setUsers(Array.isArray(usersResponse.data) ? usersResponse.data : []);
        setFilteredUsers(Array.isArray(usersResponse.data) ? usersResponse.data : []);
        setTrainingRoles(Array.isArray(rolesResponse.data) ? rolesResponse.data : []);
        setTrainingCountries(Array.isArray(countriesResponse.data) ? countriesResponse.data : []);
        setTrainingSites(Array.isArray(sitesResponse.data) ? sitesResponse.data : []);
        setCompanyUsers(Array.isArray(usersResponse.data) ? usersResponse.data : []); // For manager/supervisor/coordinator dropdowns
        setLoading(false);
      } catch (err) {
        setError('Failed to fetch users data');
        setLoading(false);
        console.error(err);
      }
    };

    fetchData();
  }, []);

  // Filter users based on search and filters
  useEffect(() => {
    let filtered = users.filter(user => {
      const matchesSearch = user.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           user.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           user.username?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesRole = roleFilter.length === 0 || 
                         roleFilter.includes(user.training_role?.toString()) ||
                         (roleFilter.includes('null') && !user.training_role);
      
      const matchesCountry = countryFilter.length === 0 || 
                            countryFilter.includes(user.training_country?.toString()) ||
                            (countryFilter.includes('null') && !user.training_country);
      
      const matchesSite = siteFilter.length === 0 || 
                         siteFilter.includes(user.training_site?.toString()) ||
                         (siteFilter.includes('null') && !user.training_site);
      
      const matchesManager = managerFilter.length === 0 || 
                            managerFilter.includes(user.manager?.toString()) ||
                            (managerFilter.includes('null') && !user.manager);
      
      const matchesSupervisor = supervisorFilter.length === 0 || 
                               supervisorFilter.includes(user.training_supervisor?.toString()) ||
                               (supervisorFilter.includes('null') && !user.training_supervisor);
      
      const matchesCoordinator = coordinatorFilter.length === 0 || 
                                coordinatorFilter.includes(user.training_coordinator?.toString()) ||
                                (coordinatorFilter.includes('null') && !user.training_coordinator);
      
      return matchesSearch && matchesRole && matchesCountry && matchesSite && matchesManager && 
             matchesSupervisor && matchesCoordinator;
    });

    // Apply sorting
    if (sortField && sortDirection) {
      filtered.sort((a, b) => {
        let aValue, bValue;

        switch (sortField) {
          case 'user':
            aValue = `${a.first_name} ${a.last_name}`.toLowerCase();
            bValue = `${b.first_name} ${b.last_name}`.toLowerCase();
            break;
          case 'role':
            aValue = a.training_role_data?.name?.toLowerCase() || '';
            bValue = b.training_role_data?.name?.toLowerCase() || '';
            break;
          case 'country':
            aValue = a.training_country_data?.name?.toLowerCase() || '';
            bValue = b.training_country_data?.name?.toLowerCase() || '';
            break;
          case 'site':
            aValue = a.training_site_data?.name?.toLowerCase() || '';
            bValue = b.training_site_data?.name?.toLowerCase() || '';
            break;
          case 'manager':
            aValue = a.manager_data ? `${a.manager_data.first_name} ${a.manager_data.last_name}`.toLowerCase() : '';
            bValue = b.manager_data ? `${b.manager_data.first_name} ${b.manager_data.last_name}`.toLowerCase() : '';
            break;
          case 'supervisor':
            aValue = a.training_supervisor_data ? `${a.training_supervisor_data.first_name} ${a.training_supervisor_data.last_name}`.toLowerCase() : '';
            bValue = b.training_supervisor_data ? `${b.training_supervisor_data.first_name} ${b.training_supervisor_data.last_name}`.toLowerCase() : '';
            break;
          case 'coordinator':
            aValue = a.training_coordinator_data ? `${a.training_coordinator_data.first_name} ${a.training_coordinator_data.last_name}`.toLowerCase() : '';
            bValue = b.training_coordinator_data ? `${b.training_coordinator_data.first_name} ${b.training_coordinator_data.last_name}`.toLowerCase() : '';
            break;
          default:
            aValue = '';
            bValue = '';
        }

        if (aValue === bValue) return 0;
        
        const result = aValue > bValue ? 1 : -1;
        return sortDirection === 'asc' ? result : -result;
      });
    }
    
    setFilteredUsers(filtered);
  }, [users, searchTerm, roleFilter, countryFilter, siteFilter, managerFilter, supervisorFilter, coordinatorFilter, sortField, sortDirection]);

  // Toggle user selection
  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  // Select all users
  const toggleSelectAll = () => {
    if (selectedUsers.length === filteredUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(filteredUsers.map(user => user.id));
    }
  };

  // Handle field change for single user or multiple users
  const handleFieldChange = (userId, field, value) => {
    if (selectedUsers.length > 1 && selectedUsers.includes(userId)) {
      // Multiple users selected - apply to all selected users
      setUserChanges(prev => {
        const updated = { ...prev };
        selectedUsers.forEach(id => {
          const changes = {
            ...updated[id],
            [field]: value
          };
          
          // Handle site-country dependencies
          if (field === 'training_site' && value && Array.isArray(trainingSites)) {
            const selectedSite = trainingSites.find(site => site.id.toString() === value);
            if (selectedSite && selectedSite.country_data) {
              changes.training_country = selectedSite.country_data.id;
            }
          } else if (field === 'training_country') {
            // If country changes, check if current site is still valid
            const currentSite = updated[id]?.training_site !== undefined 
              ? updated[id]?.training_site 
              : users.find(u => u.id === id)?.training_site;
            
            if (currentSite && value && Array.isArray(trainingSites)) {
              const site = trainingSites.find(s => s.id.toString() === currentSite.toString());
              if (site && site.country_data && site.country_data.id.toString() !== value.toString()) {
                // Site is not compatible with new country, clear it
                changes.training_site = null;
              }
            }
          }
          
          updated[id] = changes;
        });
        return updated;
      });
    } else {
      // Single user - track change
      setUserChanges(prev => {
        const changes = {
          ...prev[userId],
          [field]: value
        };
        
        // Handle site-country dependencies
        if (field === 'training_site' && value && Array.isArray(trainingSites)) {
          const selectedSite = trainingSites.find(site => site.id.toString() === value);
          if (selectedSite && selectedSite.country_data) {
            changes.training_country = selectedSite.country_data.id;
          }
        } else if (field === 'training_country') {
          // If country changes, check if current site is still valid
          const currentSite = userChanges[userId]?.training_site !== undefined 
            ? userChanges[userId]?.training_site 
            : users.find(u => u.id === userId)?.training_site;
          
          if (currentSite && value && Array.isArray(trainingSites)) {
            const site = trainingSites.find(s => s.id.toString() === currentSite.toString());
            if (site && site.country_data && site.country_data.id.toString() !== value.toString()) {
              // Site is not compatible with new country, clear it
              changes.training_site = null;
            }
          }
        }
        
        return {
          ...prev,
          [userId]: changes
        };
      });
    }
  };

  // Save all changes
  const handleSaveAllChanges = async () => {
    try {
      const userIdsWithChanges = Object.keys(userChanges);
      setSavingUsers(userIdsWithChanges);
      
      const updatePromises = userIdsWithChanges.map(userId =>
        axios.patch(`${API_BASE_URL}/users/${userId}/`, userChanges[userId], {
          headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
        })
      );

      const responses = await Promise.all(updatePromises);
      
      // Update state with all responses
      setUsers(prev => prev.map(user => {
        const response = responses.find(r => r.data.id === user.id);
        return response ? { ...user, ...response.data } : user;
      }));
      
      // Clear all changes and selected users
      setUserChanges({});
      setSelectedUsers([]);
      setSavingUsers([]);
    } catch (err) {
      setError('Failed to update users');
      setSavingUsers([]);
      console.error(err);
    }
  };

  // Bulk update users
  const handleBulkUpdate = async () => {
    try {
      const updates = {};
      if (bulkRole) updates.training_role = bulkRole;
      if (bulkCountry) updates.training_country = bulkCountry;
      if (bulkSite) {
        updates.training_site = bulkSite;
        // Auto-set country when site is selected
        if (Array.isArray(trainingSites)) {
          const selectedSite = trainingSites.find(site => site.id.toString() === bulkSite);
          if (selectedSite && selectedSite.country_data) {
            updates.training_country = selectedSite.country_data.id;
          }
        }
      }
      if (bulkManager) updates.manager = bulkManager;
      if (bulkSupervisor) updates.training_supervisor = bulkSupervisor;
      if (bulkCoordinator) updates.training_coordinator = bulkCoordinator;

      const updatePromises = selectedUsers.map(userId =>
        axios.patch(`${API_BASE_URL}/users/${userId}/`, updates, {
          headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
        })
      );

      await Promise.all(updatePromises);
      
      // Refresh users data
      const usersResponse = await axios.get(`${API_BASE_URL}/users/`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
      });
      setUsers(usersResponse.data);
      
      // Reset bulk operations
      setSelectedUsers([]);
      setShowBulkActions(false);
      setBulkRole('');
      setBulkCountry('');
      setBulkSite('');
      setBulkManager('');
      setBulkSupervisor('');
      setBulkCoordinator('');
    } catch (err) {
      setError('Failed to update users');
      console.error(err);
    }
  };

  // Get user name by ID
  const getUserName = (userId) => {
    const user = companyUsers.find(u => u.id === userId);
    return user ? `${user.first_name} ${user.last_name}` : '';
  };

  // Get role name by ID
  const getRoleName = (roleId) => {
    if (!Array.isArray(trainingRoles)) return '';
    const role = trainingRoles.find(r => r.id === roleId);
    return role ? role.name : '';
  };

  // Get country name by ID
  const getCountryName = (countryId) => {
    if (!Array.isArray(trainingCountries)) return '';
    const country = trainingCountries.find(c => c.id === countryId);
    return country ? country.name : '';
  };

  // Get site name by ID
  const getSiteName = (siteId) => {
    if (!Array.isArray(trainingSites)) return '';
    const site = trainingSites.find(s => s.id === siteId);
    return site ? site.name : '';
  };

  // Get available sites based on user's country selection
  const getAvailableSites = (userId) => {
    const user = users.find(u => u.id === userId);
    const currentCountryId = userChanges[userId]?.training_country !== undefined 
      ? userChanges[userId]?.training_country 
      : user?.training_country;
    
    if (!currentCountryId) {
      return Array.isArray(trainingSites) ? trainingSites : []; // If no country selected, show all sites
    }
    
    return Array.isArray(trainingSites) ? trainingSites.filter(site => site.country_data && site.country_data.id === currentCountryId) : [];
  };

  // Get available sites for bulk operations based on selected country
  const getBulkAvailableSites = () => {
    if (!bulkCountry) {
      return Array.isArray(trainingSites) ? trainingSites : []; // If no country selected, show all sites
    }
    
    return Array.isArray(trainingSites) ? trainingSites.filter(site => site.country_data && site.country_data.id.toString() === bulkCountry) : [];
  };

  // Handle bulk country change - clear site if incompatible
  const handleBulkCountryChange = (countryId) => {
    setBulkCountry(countryId);
    
    // Check if current bulk site is compatible with new country
    if (bulkSite && countryId && Array.isArray(trainingSites)) {
      const site = trainingSites.find(s => s.id.toString() === bulkSite);
      if (site && site.country_data && site.country_data.id.toString() !== countryId) {
        setBulkSite(''); // Clear incompatible site
      }
    }
  };

  // Handle bulk site change - auto-set country
  const handleBulkSiteChange = (siteId) => {
    setBulkSite(siteId);
    
    if (siteId && Array.isArray(trainingSites)) {
      const selectedSite = trainingSites.find(site => site.id.toString() === siteId);
      if (selectedSite && selectedSite.country_data) {
        setBulkCountry(selectedSite.country_data.id.toString());
      }
    }
  };

  // Get unique values that are actually used in the table
  const getUsedRoles = () => {
    const usedRoleIds = [...new Set(users.map(u => u.training_role).filter(Boolean))];
    return Array.isArray(trainingRoles) ? trainingRoles.filter(role => usedRoleIds.includes(role.id)) : [];
  };

  const getUsedCountries = () => {
    const usedCountryIds = [...new Set(users.map(u => u.training_country).filter(Boolean))];
    return Array.isArray(trainingCountries) ? trainingCountries.filter(country => usedCountryIds.includes(country.id)) : [];
  };

  const getUsedSites = () => {
    const usedSiteIds = [...new Set(users.map(u => u.training_site).filter(Boolean))];
    return Array.isArray(trainingSites) ? trainingSites.filter(site => usedSiteIds.includes(site.id)) : [];
  };

  const getUsedManagers = () => {
    const usedManagerIds = [...new Set(users.map(u => u.manager).filter(Boolean))];
    return Array.isArray(companyUsers) ? companyUsers.filter(user => usedManagerIds.includes(user.id)) : [];
  };

  const getUsedSupervisors = () => {
    const usedSupervisorIds = [...new Set(users.map(u => u.training_supervisor).filter(Boolean))];
    return Array.isArray(companyUsers) ? companyUsers.filter(user => usedSupervisorIds.includes(user.id)) : [];
  };

  const getUsedCoordinators = () => {
    const usedCoordinatorIds = [...new Set(users.map(u => u.training_coordinator).filter(Boolean))];
    return Array.isArray(companyUsers) ? companyUsers.filter(user => usedCoordinatorIds.includes(user.id)) : [];
  };

  // Handle multi-select filter changes
  const handleFilterChange = (filterType, value, setFilter) => {
    setFilter(prev => {
      if (prev.includes(value)) {
        return prev.filter(v => v !== value);
      } else {
        return [...prev, value];
      }
    });
  };

  // Toggle dropdown
  const toggleDropdown = (dropdownType) => {
    setDropdownOpen(prev => ({
      ...prev,
      [dropdownType]: !prev[dropdownType]
    }));
  };

  // Close all dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.dropdown-container')) {
        setDropdownOpen({
          roles: false,
          countries: false,
          managers: false,
          supervisors: false,
          coordinators: false
        });
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Multi-select dropdown component
  const MultiSelectDropdown = ({ 
    label, 
    options, 
    selectedValues, 
    onChange, 
    dropdownType,
    getOptionLabel,
    getOptionValue 
  }) => {
    const isOpen = dropdownOpen[dropdownType];
    const selectedCount = selectedValues.length;
    
    return (
      <div className="dropdown-container relative">
        <label className="block text-xs font-medium text-slate-700 mb-1">{label}</label>
        <button
          type="button"
          onClick={() => toggleDropdown(dropdownType)}
          className="w-full border border-steel-300 rounded-xl px-3 py-2 text-left focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent bg-white"
        >
          <div className="flex items-center justify-between">
            <span className="text-sm">
              {selectedCount === 0 ? `All ${label}` : `${selectedCount} selected`}
            </span>
            {isOpen ? (
              <ChevronUp className="h-4 w-4 text-slate-400" />
            ) : (
              <ChevronDown className="h-4 w-4 text-slate-400" />
            )}
          </div>
        </button>
        
        {isOpen && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-steel-300 rounded-xl shadow-lg max-h-48 overflow-y-auto">
            {/* No assignment option */}
            <label className="flex items-center px-3 py-2 hover:bg-steel-50 cursor-pointer border-b border-steel-100">
              <input
                type="checkbox"
                checked={selectedValues.includes('null')}
                onChange={() => onChange('null')}
                className="rounded border-steel-300 text-brand-500 focus:ring-brand-500 mr-2"
              />
              <span className="text-sm flex-1 text-slate-600 italic">No assignment</span>
            </label>
            
            {/* Regular options */}
            {options.map(option => {
              const value = getOptionValue(option);
              const label = getOptionLabel(option);
              const isSelected = selectedValues.includes(value);
              
              return (
                <label
                  key={value}
                  className="flex items-center px-3 py-2 hover:bg-steel-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => onChange(value)}
                    className="rounded border-steel-300 text-brand-500 focus:ring-brand-500 mr-2"
                  />
                  <span className="text-sm flex-1">{label}</span>
                </label>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 bg-steel-50 rounded-xl shadow">
        <div className="text-center">
          <Loader2 className="h-10 w-10 animate-spin text-brand-500 mx-auto mb-4" />
          <p className="text-slate-600 font-medium">Loading users...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 m-4">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
            <p className="text-red-700">{error}</p>
            <button
              onClick={() => setError(null)}
              className="ml-auto text-red-400 hover:text-red-600"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <div className="px-6 pt-4 pb-2 space-y-4">
        {/* First Row: Search, Sites, Countries, and Save Button */}
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search - 1/2 width normally, shrinks when Save button appears */}
          <div className={`relative min-w-0 ${Object.keys(userChanges).length > 0 ? 'lg:w-2/5' : 'lg:w-1/2'}`}>
            <label className="block text-xs font-medium text-slate-700 mb-1">Search</label>
            <div className="relative">
              <Search className="h-5 w-5 text-slate-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
              <input
                type="text"
                placeholder="Search users by name or username..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full border border-steel-300 rounded-xl pl-10 pr-4 py-2 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all"
              />
            </div>
          </div>

          {/* Sites and Countries - each 1/4 width */}
          <div className="lg:w-1/4">
            <MultiSelectDropdown
              label="Sites"
              options={getUsedSites()}
              selectedValues={siteFilter}
              onChange={(value) => handleFilterChange('site', value, setSiteFilter)}
              dropdownType="sites"
              getOptionLabel={(option) => option.name}
              getOptionValue={(option) => option.id.toString()}
            />
          </div>

          <div className="lg:w-1/4">
            <MultiSelectDropdown
              label="Countries"
              options={getUsedCountries()}
              selectedValues={countryFilter}
              onChange={(value) => handleFilterChange('country', value, setCountryFilter)}
              dropdownType="countries"
              getOptionLabel={(option) => option.name}
              getOptionValue={(option) => option.id.toString()}
            />
          </div>
          
          {/* Save button - appears when there are changes */}
          {Object.keys(userChanges).length > 0 && (
            <div className="lg:w-1/5 flex items-end">
              <button
                onClick={handleSaveAllChanges}
                disabled={savingUsers.length > 0}
                className="bg-green-600 text-white px-4 py-2 rounded-xl hover:bg-green-700 transition-colors font-medium disabled:opacity-50 whitespace-nowrap w-full"
              >
                {savingUsers.length > 0 ? 'Saving...' : `Save (${Object.keys(userChanges).length})`}
              </button>
            </div>
          )}
        </div>

        {/* Second Row: 4 Filters - each 1/4 width */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <MultiSelectDropdown
            label="Roles"
            options={getUsedRoles()}
            selectedValues={roleFilter}
            onChange={(value) => handleFilterChange('role', value, setRoleFilter)}
            dropdownType="roles"
            getOptionLabel={(option) => option.name}
            getOptionValue={(option) => option.id.toString()}
          />

          <MultiSelectDropdown
            label="Managers"
            options={getUsedManagers()}
            selectedValues={managerFilter}
            onChange={(value) => handleFilterChange('manager', value, setManagerFilter)}
            dropdownType="managers"
            getOptionLabel={(option) => `${option.first_name} ${option.last_name}`}
            getOptionValue={(option) => option.id.toString()}
          />

          <MultiSelectDropdown
            label="Supervisors"
            options={getUsedSupervisors()}
            selectedValues={supervisorFilter}
            onChange={(value) => handleFilterChange('supervisor', value, setSupervisorFilter)}
            dropdownType="supervisors"
            getOptionLabel={(option) => `${option.first_name} ${option.last_name}`}
            getOptionValue={(option) => option.id.toString()}
          />

          <MultiSelectDropdown
            label="Coordinators"
            options={getUsedCoordinators()}
            selectedValues={coordinatorFilter}
            onChange={(value) => handleFilterChange('coordinator', value, setCoordinatorFilter)}
            dropdownType="coordinators"
            getOptionLabel={(option) => `${option.first_name} ${option.last_name}`}
            getOptionValue={(option) => option.id.toString()}
          />
        </div>

        {/* Bulk Actions */}
        {selectedUsers.length > 0 && (
          <div className="bg-brand-50 border border-brand-200 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-brand-700 font-medium">
                {selectedUsers.length} user{selectedUsers.length > 1 ? 's' : ''} selected
              </span>
              <button
                onClick={() => setShowBulkActions(!showBulkActions)}
                className="text-brand-500 hover:text-brand-600 font-medium"
              >
                Bulk Actions <ChevronDown className="h-4 w-4 inline ml-1" />
              </button>
            </div>
            
            {showBulkActions && (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-3">
                <select
                  value={bulkRole}
                  onChange={(e) => setBulkRole(e.target.value)}
                  className="border border-steel-300 rounded px-3 py-2 text-sm"
                >
                  <option value="">Role...</option>
                  {trainingRoles.map(role => (
                    <option key={role.id} value={role.id}>{role.name}</option>
                  ))}
                </select>

                <select
                  value={bulkCountry}
                  onChange={(e) => handleBulkCountryChange(e.target.value)}
                  className="border border-steel-300 rounded px-3 py-2 text-sm"
                >
                  <option value="">Country...</option>
                  {trainingCountries.map(country => (
                    <option key={country.id} value={country.id}>{country.name}</option>
                  ))}
                </select>

                <select
                  value={bulkSite}
                  onChange={(e) => handleBulkSiteChange(e.target.value)}
                  className="border border-steel-300 rounded px-3 py-2 text-sm"
                >
                  <option value="">Site...</option>
                  {getBulkAvailableSites().map(site => (
                    <option key={site.id} value={site.id}>{site.name}</option>
                  ))}
                </select>

                <select
                  value={bulkManager}
                  onChange={(e) => setBulkManager(e.target.value)}
                  className="border border-steel-300 rounded px-3 py-2 text-sm"
                >
                  <option value="">Manager...</option>
                  {companyUsers.map(user => (
                    <option key={user.id} value={user.id}>{user.first_name} {user.last_name}</option>
                  ))}
                </select>

                <select
                  value={bulkSupervisor}
                  onChange={(e) => setBulkSupervisor(e.target.value)}
                  className="border border-steel-300 rounded px-3 py-2 text-sm"
                >
                  <option value="">Supervisor...</option>
                  {companyUsers.map(user => (
                    <option key={user.id} value={user.id}>{user.first_name} {user.last_name}</option>
                  ))}
                </select>

                <select
                  value={bulkCoordinator}
                  onChange={(e) => setBulkCoordinator(e.target.value)}
                  className="border border-steel-300 rounded px-3 py-2 text-sm"
                >
                  <option value="">Coordinator...</option>
                  {companyUsers.map(user => (
                    <option key={user.id} value={user.id}>{user.first_name} {user.last_name}</option>
                  ))}
                </select>
              </div>
            )}
            
            {showBulkActions && (
              <div className="flex space-x-3">
                <button
                  onClick={handleBulkUpdate}
                  className="bg-brand-500 text-white px-4 py-2 rounded-xl hover:bg-brand-600 transition-colors text-sm font-medium"
                >
                  Update Selected
                </button>
                <button
                  onClick={() => {
                    setSelectedUsers([]);
                    setShowBulkActions(false);
                  }}
                  className="bg-slate-600 text-white px-4 py-2 rounded-xl hover:bg-slate-700 transition-colors text-sm font-medium"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Users Table */}
      <div className="flex-1 px-6 pb-6">
        {filteredUsers.length === 0 ? (
          <div className="text-center py-12">
            <Users className="h-12 w-12 text-slate-400 mx-auto mb-4" />
            <p className="text-slate-600 font-medium mb-2">No users found</p>
            <p className="text-slate-500 text-sm">
              {searchTerm || roleFilter || countryFilter || managerFilter || supervisorFilter || coordinatorFilter
                ? 'Try adjusting your search terms or filters.'
                : 'No users available for your company.'}
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-steel-200 overflow-hidden">
            <div className="overflow-auto max-h-[68vh]">
              <table className="min-w-full divide-y divide-steel-200">
                <thead className="bg-steel-50 sticky top-0 z-[5] shadow-sm border-b border-steel-200">
                  <tr>
                    <th className="px-2 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={selectedUsers.length === filteredUsers.length && filteredUsers.length > 0}
                        onChange={toggleSelectAll}
                        className="rounded border-steel-300 text-brand-500 focus:ring-brand-500"
                      />
                    </th>
                    <th className="px-2 py-3 text-left">
                      <button
                        onClick={() => handleSort('user')}
                        className="flex items-center text-xs font-medium text-slate-500 uppercase tracking-wider hover:text-slate-700 transition-colors"
                      >
                        User
                        <span className="ml-1">{getSortIcon('user')}</span>
                      </button>
                    </th>
                    <th className="px-6 py-3 text-left">
                      <button
                        onClick={() => handleSort('role')}
                        className="flex items-center text-xs font-medium text-slate-500 uppercase tracking-wider hover:text-slate-700 transition-colors"
                      >
                        Role
                        <span className="ml-1">{getSortIcon('role')}</span>
                      </button>
                    </th>
                    <th className="px-6 py-3 text-left">
                      <button
                        onClick={() => handleSort('country')}
                        className="flex items-center text-xs font-medium text-slate-500 uppercase tracking-wider hover:text-slate-700 transition-colors"
                      >
                        Country
                        <span className="ml-1">{getSortIcon('country')}</span>
                      </button>
                    </th>
                    <th className="px-6 py-3 text-left">
                      <button
                        onClick={() => handleSort('site')}
                        className="flex items-center text-xs font-medium text-slate-500 uppercase tracking-wider hover:text-slate-700 transition-colors"
                      >
                        Site
                        <span className="ml-1">{getSortIcon('site')}</span>
                      </button>
                    </th>
                    <th className="px-6 py-3 text-left">
                      <button
                        onClick={() => handleSort('manager')}
                        className="flex items-center text-xs font-medium text-slate-500 uppercase tracking-wider hover:text-slate-700 transition-colors"
                      >
                        Manager
                        <span className="ml-1">{getSortIcon('manager')}</span>
                      </button>
                    </th>
                    <th className="px-6 py-3 text-left">
                      <button
                        onClick={() => handleSort('supervisor')}
                        className="flex items-center text-xs font-medium text-slate-500 uppercase tracking-wider hover:text-slate-700 transition-colors"
                      >
                        Supervisor
                        <span className="ml-1">{getSortIcon('supervisor')}</span>
                      </button>
                    </th>
                    <th className="px-6 py-3 text-left">
                      <button
                        onClick={() => handleSort('coordinator')}
                        className="flex items-center text-xs font-medium text-slate-500 uppercase tracking-wider hover:text-slate-700 transition-colors"
                      >
                        Coordinator
                        <span className="ml-1">{getSortIcon('coordinator')}</span>
                      </button>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-steel-200">
                  {filteredUsers.map((user) => {
                    const hasChanges = userChanges[user.id];
                    const isSaving = savingUsers.includes(user.id);
                    
                    return (
                      <tr key={user.id} className="hover:bg-steel-50">
                        <td className="px-2 py-4">
                          <input
                            type="checkbox"
                            checked={selectedUsers.includes(user.id)}
                            onChange={() => toggleUserSelection(user.id)}
                            className="rounded border-steel-300 text-brand-500 focus:ring-brand-500"
                          />
                        </td>
                        <td className="px-2 py-4">
                          <div>
                            <div className="text-sm font-medium text-slate-900">
                              {user.first_name} {user.last_name}
                            </div>
                            <div className="text-sm text-slate-500">({user.username})</div>
                            {hasChanges && (
                              <div className="text-xs text-brand-500 mt-1">• Has changes</div>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-4 text-sm text-slate-900">
                          <select
                            value={userChanges[user.id]?.training_role !== undefined ? (userChanges[user.id]?.training_role || '') : (user.training_role || '')}
                            onChange={(e) => handleFieldChange(user.id, 'training_role', e.target.value || null)}
                            disabled={isSaving}
                            className="border border-steel-300 rounded px-2 py-1 text-sm w-full disabled:opacity-50"
                          >
                            <option value="">No assignment</option>
                            {trainingRoles.map(role => (
                              <option key={role.id} value={role.id}>{role.name}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-3 py-4 text-sm text-slate-900">
                          <select
                            value={userChanges[user.id]?.training_country !== undefined ? (userChanges[user.id]?.training_country || '') : (user.training_country || '')}
                            onChange={(e) => handleFieldChange(user.id, 'training_country', e.target.value || null)}
                            disabled={isSaving}
                            className="border border-steel-300 rounded px-2 py-1 text-sm w-full disabled:opacity-50"
                          >
                            <option value="">No assignment</option>
                            {trainingCountries.map(country => (
                              <option key={country.id} value={country.id}>{country.name}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-3 py-4 text-sm text-slate-900">
                          <select
                            value={userChanges[user.id]?.training_site !== undefined ? (userChanges[user.id]?.training_site || '') : (user.training_site || '')}
                            onChange={(e) => handleFieldChange(user.id, 'training_site', e.target.value || null)}
                            disabled={isSaving}
                            className="border border-steel-300 rounded px-2 py-1 text-sm w-full disabled:opacity-50"
                          >
                            <option value="">No assignment</option>
                            {getAvailableSites(user.id).map(site => (
                              <option key={site.id} value={site.id}>{site.name}</option>
                            ))}
                            {/* Show current site even if it's not in filtered list (with indication) */}
                            {(() => {
                              const currentSiteId = userChanges[user.id]?.training_site !== undefined 
                                ? userChanges[user.id]?.training_site 
                                : user.training_site;
                              const availableSites = getAvailableSites(user.id);
                              const currentSite = Array.isArray(trainingSites) ? trainingSites.find(s => s.id === currentSiteId) : null;
                              
                              if (currentSite && !availableSites.find(s => s.id === currentSiteId)) {
                                return (
                                  <option key={`invalid-${currentSite.id}`} value={currentSite.id} style={{color: '#dc2626', fontStyle: 'italic'}}>
                                    {currentSite.name} (incompatible country)
                                  </option>
                                );
                              }
                              return null;
                            })()}
                          </select>
                        </td>
                        <td className="px-3 py-4 text-sm text-slate-900">
                          <select
                            value={userChanges[user.id]?.manager !== undefined ? (userChanges[user.id]?.manager || '') : (user.manager || '')}
                            onChange={(e) => handleFieldChange(user.id, 'manager', e.target.value || null)}
                            disabled={isSaving}
                            className="border border-steel-300 rounded px-2 py-1 text-sm w-full disabled:opacity-50"
                          >
                            <option value="">No assignment</option>
                            {companyUsers.map(u => (
                              <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-3 py-4 text-sm text-slate-900">
                          <select
                            value={userChanges[user.id]?.training_supervisor !== undefined ? (userChanges[user.id]?.training_supervisor || '') : (user.training_supervisor || '')}
                            onChange={(e) => handleFieldChange(user.id, 'training_supervisor', e.target.value || null)}
                            disabled={isSaving}
                            className="border border-steel-300 rounded px-2 py-1 text-sm w-full disabled:opacity-50"
                          >
                            <option value="">No assignment</option>
                            {companyUsers.map(u => (
                              <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>
                            ))}
                          </select>
                        </td>
                        <td className="px-3 py-4 text-sm text-slate-900">
                          <select
                            value={userChanges[user.id]?.training_coordinator !== undefined ? (userChanges[user.id]?.training_coordinator || '') : (user.training_coordinator || '')}
                            onChange={(e) => handleFieldChange(user.id, 'training_coordinator', e.target.value || null)}
                            disabled={isSaving}
                            className="border border-steel-300 rounded px-2 py-1 text-sm w-full disabled:opacity-50"
                          >
                            <option value="">No assignment</option>
                            {companyUsers.map(u => (
                              <option key={u.id} value={u.id}>{u.first_name} {u.last_name}</option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TrainingUsersContent;